import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaPlus,
  FaMinus,
  FaBolt,
} from "react-icons/fa";

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // FAQ DATA
  const faqData = [
    {
      question: "What is Power Sense and how does it help?",
      answer:
        "Power Sense provides real-time energy monitoring, IoT automation, and power analytics for campuses, industries, and residential communities. Our platform improves reliability, reduces downtime, and cuts operational energy costs.",
    },
    {
      question: "How does Power Sense reduce electricity bills?",
      answer:
        "Our system tracks energy consumption patterns, identifies abnormal loads, detects wastage, and provides actionable analytics to optimize energy efficiency across infrastructure.",
    },
    {
      question: "Can Power Sense integrate with existing systems?",
      answer:
        "Yes. Power Sense integrates with smart meters, UPS systems, IoT devices, diesel generator monitoring systems, and industrial sensors without major infrastructure changes.",
    },
    {
      question: "Is my energy data secure with Power Sense?",
      answer:
        "Absolutely. All data is encrypted, securely transmitted, and stored in enterprise-grade cloud infrastructure following modern security standards and IoT best practices.",
    },
    {
      question: "Do you provide training and certification programs?",
      answer:
        "Yes. Power Sense offers IITH-certified training programs in Energy Management, Smart Grid Systems, IoT Technologies, and Data Interpretation led by Prof. Pradeep Yamula.",
    },
  ];

  const handleGetStarted = () => {
    navigate("/enquiry");
  };

  return (
    <div className="relative overflow-hidden text-slate-200 min-h-screen">

      {/* BACKGROUND */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0b0f19] to-[#111827] opacity-95"></div>

      {/* Glow Waves */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div
          className="absolute top-0 left-0 w-[120%] h-[120%]
          bg-gradient-to-r from-orange-500/30 via-yellow-400/20 to-sky-500/30
          blur-3xl animate-slowWave"
        ></div>

        <div
          className="absolute bottom-0 right-0 w-[120%] h-[120%]
          bg-gradient-to-r from-sky-500/20 via-orange-300/20 to-yellow-400/30
          blur-3xl animate-fastWave"
        ></div>
      </div>

      {/* Grid */}
      <div
        className="absolute inset-0 pointer-events-none
        bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),
             linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)]
        bg-[size:60px_60px]"
      ></div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <span
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-20 animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${2 + Math.random() * 4}s`,
            }}
          ></span>
        ))}
      </div>

      {/* FAQ SECTION */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">

        {/* Header */}
        <div className="text-center mb-16">

          <div className="inline-flex items-center px-5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl mb-5">
            <FaBolt className="text-yellow-400 mr-2" />
            <span className="text-[11px] tracking-[4px] uppercase text-slate-300">
              Power Sense Support
            </span>
          </div>

          <h1
            className="text-4xl md:text-6xl font-black mb-6
            bg-gradient-to-r from-orange-300 via-yellow-200 to-cyan-300
            text-transparent bg-clip-text animate-gradientMove bg-[length:200%_200%]"
          >
            Frequently Asked Questions
          </h1>

          <p className="max-w-3xl mx-auto text-slate-400 text-lg leading-relaxed">
            Everything you need to know about Power Sense smart energy
            monitoring, analytics, IoT integration, and enterprise solutions.
          </p>
        </div>

        {/* FAQ CARDS */}
        <div className="space-y-5 max-w-5xl mx-auto">

          {faqData.map((faq, index) => (
            <div
              key={index}
              className={`group relative overflow-hidden rounded-3xl border
              transition-all duration-500 backdrop-blur-2xl
              ${
                activeIndex === index
                  ? "border-yellow-400/30 bg-gradient-to-br from-[#1a1408]/90 via-[#22180b]/80 to-[#0f172a]/90 shadow-[0_0_40px_rgba(255,196,0,0.15)]"
                  : "border-white/10 bg-[#121826]/70 hover:border-white/20 hover:-translate-y-1"
              }`}
            >

              {/* Glow */}
              <div
                className={`absolute inset-0 opacity-0 group-hover:opacity-20
                transition-all duration-700
                blur-3xl
                ${
                  activeIndex === index
                    ? "bg-gradient-to-r from-yellow-400 to-orange-500"
                    : "bg-gradient-to-r from-sky-400 to-cyan-500"
                }`}
              ></div>

              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="relative z-10 w-full flex items-center justify-between p-5 text-left"
              >

                <div className="flex items-center gap-5">

                  {/* Number */}
                  <div
                    className={`min-w-[55px] h-[55px] rounded-2xl
                    flex items-center justify-center font-bold text-lg
                    transition-all duration-500
                    ${
                      activeIndex === index
                        ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-black shadow-lg"
                        : "bg-white/5 text-slate-300 border border-white/10"
                    }`}
                  >
                    0{index + 1}
                  </div>

                  {/* Question */}
                  <div>
                    <h3
                      className={`text-lg md:text-xl font-semibold transition-all duration-300
                      ${
                        activeIndex === index
                          ? "text-yellow-300"
                          : "text-white"
                      }`}
                    >
                      {faq.question}
                    </h3>

                    <p className="text-sm text-slate-400 mt-1">
                      Click to view detailed answer
                    </p>
                  </div>
                </div>

                {/* Icon */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center
                  transition-all duration-500
                  ${
                    activeIndex === index
                      ? "bg-yellow-400 text-black rotate-180"
                      : "bg-white/5 text-white"
                  }`}
                >
                  {activeIndex === index ? (
                    <FaMinus size={14} />
                  ) : (
                    <FaPlus size={14} />
                  )}
                </div>
              </button>

              {/* Answer */}
              <div
                className={`grid transition-all duration-700 ease-in-out
                ${
                  activeIndex === index
                    ? "grid-rows-[1fr] opacity-100"
                    : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="relative z-10 px-7 pb-7 pl-[95px]">
                    <div className="h-[1px] w-full bg-white/10 mb-5"></div>

                    <p className="text-slate-300 leading-relaxed text-[15px]">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* CTA SECTION */}
      <div className="relative z-10 mt-10">

        {/* Glow */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute left-0 top-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute right-0 bottom-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        </div>

        <div
          className="relative bg-gradient-to-r from-[#f59e0b] via-[#fbbf24] to-[#f97316]
          rounded-t-[4rem] overflow-hidden"
        >

          <div className="max-w-7xl mx-auto px-6 py-24 relative">

            <div className="grid md:grid-cols-2 gap-16 items-center">

              {/* LEFT */}
              <div>

                <div className="inline-flex items-center px-4 py-2 rounded-full bg-black/10 border border-black/10 mb-6">
                  <span className="text-[11px] tracking-[3px] uppercase text-black font-semibold">
                    Smart Energy Future
                  </span>
                </div>

                <h2 className="text-4xl md:text-5xl font-black text-black mb-6 leading-tight">
                  Ready to Transform Your Energy Infrastructure?
                </h2>

                <p className="text-black/80 text-lg leading-relaxed mb-8 max-w-xl">
                  Power Sense delivers real-time monitoring, smart analytics,
                  automation, and certified training programs for modern energy
                  ecosystems.
                </p>

                <button
                  onClick={handleGetStarted}
                  className="group bg-black text-yellow-400 px-8 py-4 rounded-2xl
                  font-semibold hover:bg-black/90 transition-all duration-500
                  inline-flex items-center shadow-2xl hover:scale-105"
                >
                  Contact Us

                  <FaArrowRight
                    className="ml-3 transition-transform duration-500
                    group-hover:translate-x-2"
                  />
                </button>
              </div>

              {/* RIGHT UI CARD */}
              <div className="relative">

                {/* Floating Background Layers */}
                <div className="absolute inset-0 bg-black/20 rounded-3xl backdrop-blur-md rotate-6"></div>
                <div className="absolute inset-0 bg-black/10 rounded-3xl backdrop-blur-md -rotate-3"></div>

                {/* Main Card */}
                <div
                  className="relative z-10 bg-white/10 border border-white/20
                  backdrop-blur-2xl rounded-3xl p-8 shadow-2xl"
                >

                  {/* Top dots */}
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>

                  {/* Fake dashboard */}
                  <div className="space-y-4">

                    <div className="h-4 rounded-full bg-white/20 w-full"></div>

                    <div className="h-4 rounded-full bg-white/20 w-4/5"></div>

                    <div className="h-4 rounded-full bg-white/20 w-3/5"></div>

                    <div className="grid grid-cols-3 gap-4 mt-8">

                      <div className="h-24 rounded-2xl bg-yellow-500/30 border border-yellow-400/30"></div>

                      <div className="h-24 rounded-2xl bg-orange-500/30 border border-orange-400/30"></div>

                      <div className="h-24 rounded-2xl bg-sky-500/30 border border-sky-400/30"></div>
                    </div>

                    <div className="h-32 rounded-3xl mt-6 bg-gradient-to-r from-black/30 to-black/10 border border-white/10"></div>

                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`

        @keyframes slowWave {
          0% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
          50% {
            transform: translate(10%, 10%) rotate(14deg);
          }
          100% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
        }

        @keyframes fastWave {
          0% {
            transform: translate(10%, 10%) rotate(0deg);
          }
          50% {
            transform: translate(-10%, -10%) rotate(-14deg);
          }
          100% {
            transform: translate(10%, 10%) rotate(0deg);
          }
        }

        @keyframes gradientMove {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animate-gradientMove {
          animation: gradientMove 6s ease infinite;
        }

        .animate-slowWave {
          animation: slowWave 14s ease-in-out infinite;
        }

        .animate-fastWave {
          animation: fastWave 10s ease-in-out infinite;
        }

      `}</style>
    </div>
  );
};

export default FAQ;